import { Call, RecordSettingsRequestModeEnum, StreamVideoClient, User } from '@stream-io/video-react-sdk';
import { StreamChat } from 'stream-chat';
import { CallStreamData } from '../store/slices/authSlice';

export class StreamService {
    private chatClient: StreamChat;
    private videoClient: StreamVideoClient | null = null;
    private currentCall: Call | null = null;
    private isJoiningCall: boolean = false;
    constructor() {
        const apiKey = process.env.REACT_APP_STREAM_API_KEY ?? '';
        this.chatClient = StreamChat.getInstance(apiKey, {
            baseURL: 'https://chat.stream-chat.com',
            timeout: 30000,
        });
    }

    getVideoClient() {
        return this.videoClient;
    }

    getChatClient() {
        return this.chatClient;
    }

    async connectUser(userId: string, streamToken: string) {
        await this.chatClient.connectUser(
            {
                id: userId
            },
            streamToken
        );

        const apiKey = process.env.REACT_APP_STREAM_API_KEY ?? '';
        const user: User = { id: userId };
        const token = streamToken;
        this.videoClient = new StreamVideoClient({ apiKey, token, user });

        return {
            chatClient: this.chatClient,
            videoClient: this.videoClient
        };
    }

    async disconnectUser() {
        if (this.chatClient)
            await this.chatClient.disconnectUser();

        if (this.videoClient)
            await this.videoClient.disconnectUser();
    }

    setCurrentCall(call: Call | null) {
        this.currentCall = call;
    }

    getCurrentCall() : Call | undefined {
        return this.currentCall ?? undefined;
    }

    async startCall(currentUserId: string, userId: string): Promise<string> {
        const client = streamService.getVideoClient();

        if(! client)
            throw new Error('Client not found');

        const callId = Math.random().toString(36).substring(2) + Date.now().toString(36);
        const callData = { 
            create: true,
            members_limit: 2,
            data: {
                members: [
                    { user_id: currentUserId, role: 'admin' },
                    { user_id: userId, role: 'admin' }
                ],
                settings_override: {
                    recording: {
                        mode: RecordSettingsRequestModeEnum.DISABLED
                    },
                    ring: {
                        auto_cancel_timeout_ms: 20000,
                        incoming_call_timeout_ms: 20000,
                        missed_call_timeout_ms: 20000
                    }
                }
            },
            ring: true
        }

        if(this.currentCall != null) {
            try{
                await this.currentCall.leave();
            } catch(error) {
                console.error("Failed to leave the call", error);
            }
        }

        const call = client.call('audio_room', callId);
        console.log(`New call created ${callId}`);

        await call.microphone.enable();

        return new Promise(async (resolve, reject) => {
            call
                .getOrCreate(callData)
                .then(() => {
                    this.setCurrentCall(call);
                    resolve(callId);
                },
                (error) => {
                    console.error(`Failed to join call`, error);
                    reject(error);
                }
            );
        });
    }

    async leaveCall() {
        if(this.currentCall) {
            try {
                await this.currentCall.endCall();
            } catch(error) {
                console.error("Failed to end the call", error);
            } finally {
                this.setCurrentCall(null);
            }
        }
    }
}

export const getCallStreamDataFromCall = (call: Call | null, currentPlebId: string) : CallStreamData | null => {
    let ret : CallStreamData | null = null;

    if(call) {
        const calledUser = call?.state?.members.find((member) => member.user.id !== currentPlebId);

        if(calledUser) {
            ret = {
                id: call.id,
                name: calledUser?.user.name ?? '',
                image: calledUser?.user.image ?? '',
                userId: calledUser?.user.id ?? ''
            }
        }
    }

    return ret;
}

export const streamService = new StreamService();